import { useState, useCallback, useMemo } from 'react';
import { productApprovalsApi } from '../data/productApprovalsApi';
import { captureException, reportRequestException } from '../utils/sentryUtils';
import { useGetApprovalConfigurationQuery } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsConfigurationRpc.hooks';
import { useGetIsUngated } from './useGetIsUngated';
import { GATES } from '../constants/auth';
export const useApprovalConfig = (approvalType, approvalAction) => {
  const getIsUngated = useGetIsUngated();
  const isChirpEnabled = getIsUngated(GATES.CHIRP_MIGRATION);
  const [isLoading, setIsLoading] = useState(false);
  const [config, setConfig] = useState({
    approvalAction,
    approvalType,
    enabled: null,
    portalId: null
  });
  const fetchConfig = useCallback(callback => {
    if (isChirpEnabled) return;
    setIsLoading(true);
    productApprovalsApi.fetchProductApprovalsConfig({
      approvalType,
      approvalAction
    }).then(response => {
      setConfig(prev => Object.assign({}, prev, response));
      if (callback && typeof callback === 'function') {
        callback(response);
      }
    }).catch(errorResponse => {
      // If we cannot get a response from config endpoint, we will set approvals to enabled:false
      setConfig(prev => Object.assign({}, prev, {
        enabled: false
      }));
      reportRequestException('fetchConfig', errorResponse);
    }).finally(() => {
      setIsLoading(false);
    }).catch(() => {
      console.error('Error fetching approvals');
    });
  }, [approvalAction, approvalType, isChirpEnabled]);
  const {
    data: chirpData,
    loading: chirpLoading,
    error: chirpError,
    refetch: chirpRefetch
  } = useGetApprovalConfigurationQuery({
    variables: {
      approvalType,
      approvalAction
    },
    skip: !isChirpEnabled,
    // fetchPolicy 'network-only' ensures fresh data. If removing this need to test extensively
    fetchPolicy: 'network-only',
    onError: error => {
      captureException(error, {
        extra: {
          fingerprint: 'useApprovalConfig-chirp',
          approvalType,
          approvalAction
        }
      });
    }
  });
  const chirpConfig = useMemo(() => {
    if (!chirpData) {
      // If we cannot get a response from config endpoint, we will set approvals to enabled:false
      const enabled = chirpError ? false : null;
      return {
        approvalAction,
        approvalType,
        enabled,
        portalId: null
      };
    }
    const {
      approvalConfiguration
    } = chirpData;
    return {
      // We already have the approvalAction and approvalType so don't need to use the chirp values,
      // this also avoids the TS error from unhandled enum case. In future I want to remove all FE types tho
      approvalAction,
      approvalType,
      enabled: approvalConfiguration.enabled,
      portalId: approvalConfiguration.portalId
    };
  }, [chirpData, chirpError, approvalAction, approvalType]);
  const chirpFetchConfig = useCallback(() => {
    void chirpRefetch();
  }, [chirpRefetch]);
  return useMemo(() => {
    if (isChirpEnabled) {
      return {
        config: chirpConfig,
        fetchConfig: chirpFetchConfig,
        isLoading: chirpLoading
      };
    }
    return {
      config,
      fetchConfig,
      isLoading
    };
  }, [isChirpEnabled, chirpConfig, chirpFetchConfig, chirpLoading, config, fetchConfig, isLoading]);
};